<template>
    <div class="flex flex-col h-full gap-2 p-2">
        <ai-grid-edit
            ref="eofiderGrid"
            :action="action"
            :args="args"
            :dots-click="dotsClick"
            :locked-rows="lockedRows"
            :meta="meta"
            @userDataChanged="onUserDataChanged"
        />
    </div>
</template>
<script setup>
import AiGridEdit from '@/components/ai-grid-edit/ai-grid-edit.vue';
import { useRoute } from 'vue-router';
import { onMounted, ref } from 'vue';

const props = defineProps({
    action: {
        type: Boolean,
        default: null,
    },
    userData: {
        type: Object,
        default: () => {
            return {};
        },
    },
});

const route = useRoute();
const eofiderGrid = ref();
const args = {
    select: {
        select_args: {
            eobj_id: route.params.id,
        },
    },
    save: {
        insert_args: {
            parent_id: route.params.id,
        },
    },
};
const meta = {
    keyExpr: 'eobj_id',
    viewId: 'widgetEofider',
    columns: [
        {
            dataField: 'dnum',
            caption: 'Диспетчерский №',
            width: 150,
        },
        {
            dataField: 'fname',
            caption: 'Оперативное наимен.',
            width: 150,
        },
        {
            dataField: 'bal_id',
            caption: 'Балансовая принадлежность',
            width: 150,
            dataType: 'number',
            editor: { type: 'ai-popup', text: 'bname' },
            component: {
                id: 'kodp',
                text: 'name',
                path: 'components/dictionary-components/ai-dictionary-virtual-grid.vue',
                saveButtonVisible: false,
                title: 'Выбор балансовой принадлежности',
                meta: {
                    columns: [
                        { field: 'kodp', text: 'kodp', visible: false },
                        { field: 'enscope_name', text: 'enscope_name', visible: false },
                        { field: 'sname', text: 'Код', width: 100 },
                        { field: 'name', text: 'Наименование', width: 250 },
                        { field: 'kod_separator', text: 'kod_separator', visible: false },
                        { field: 'kod_ecls', text: 'kod_ecls', visible: false },
                        { field: 'kod_parent', text: 'kod_parent', visible: false },
                        { field: 'inn', text: 'ИНН', width: 100 },
                        { field: 'kpp', text: 'КПП', width: 100 },
                        { field: 'addr', text: 'Адрес юридический' },
                        { field: 'pr_active', text: 'pr_active', data_type: 'boolean', visible: false },
                    ],
                    loadUrl: 'e-grid./bal/data/get',
                    keyExpr: 'kodp',
                    autoLoadData: false,
                    toRemoveToolbarItemsNames: ['copy', 'edit', 'add', 'delete', 'save'],
                },
            },
        },
        {
            dataField: 'bkod_separator',
            width: 150,
            visible: false,
            dataType: 'number',
            allowEditing: false,
        },
        {
            dataField: 'bkod_ecls',
            width: 150,
            visible: false,
            dataType: 'number',
            allowEditing: false,
        },
        {
            dataField: 'bname',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'expl_id',
            caption: 'Эксплуатационная ответственность',
            width: 150,
            dataType: 'number',
            editor: { type: 'ai-popup', text: 'ename' },
            component: {
                id: 'kodp',
                text: 'name',
                saveButtonVisible: false,
                title: 'Выбор эксплуатационной ответственности',
                path: 'views/e-grid/eosrc-card/field-wrappers/component-ge-expl-id-vg.vue',
            },
        },
        {
            dataField: 'ename',
            width: 150,
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'tech_location_code',
            caption: 'Код технического места ЛЭП в SAP (или ее аналога)',
            width: 150,
            visible: false,
        },
        {
            dataField: 'imported',
            caption: 'Импортирован',
            width: 150,
            dataType: 'boolean',
            allowEditing: false,
        },
        {
            dataField: 'kod_line',
            caption: 'Исполнение линии',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'tlinename',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_line',
                    columns: [
                        { caption: 'kod_line', visible: false, dataField: 'kod_line' },
                        {
                            caption: 'Исполнение линии',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./typeline/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_typeline' },
                },
            },
        },
        {
            dataField: 'kod_achr2',
            caption: 'АЧР2',
            width: 150,
            editor: { type: 'ai-popup', text: 'achr2name' },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                id: 'kod_achr2',
                text: 'fname',
                title: 'Выбор АЧР2',
                meta: {
                    title: '',
                    keyExpr: 'kod_achr2',
                    columns: [
                        { caption: 'kod_achr2', visible: false, dataField: 'kod_achr2' },
                        { caption: 'fname', visible: false, dataField: 'fname' },
                        { caption: 'Наименование', dataField: 'name' },
                        { caption: 'Энергорайон', dataField: 'sname' },
                        { caption: 't срабатывания (сек)', dataField: 't' },
                        { caption: 'f срабатывания (Гц)', dataField: 'fgc' },
                    ],
                    api: { select: 'e-grid./achr2/get' },
                },
                args: {},
            },
        },
        {
            dataField: 'kod_chapv',
            caption: 'ЧАПВ',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'chapvname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_chapv',
                    columns: [
                        { caption: 'kod_chapv', visible: false, dataField: 'kod_chapv' },
                        {
                            caption: 'ЧАПВ',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./chapv/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_chapv' },
                },
            },
        },
        {
            dataField: 'kod_saon',
            caption: 'САОН',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'saonname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_saon',
                    columns: [
                        { caption: 'kod_saon', visible: false, dataField: 'kod_saon' },
                        {
                            caption: 'САОН',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./saon/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_saon' },
                },
            },
        },
        {
            dataField: 'kod_otklfid',
            caption: 'Оч. отключения (общ)',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'otklfidname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_otklfid',
                    columns: [
                        {
                            caption: 'kod_otklfid',
                            visible: false,
                            dataField: 'kod_otklfid',
                        },
                        { caption: 'Оч. отключения (общ)', dataField: 'name' },
                    ],
                    api: { select: 'e-grid./ochotkl/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_ochotkl' },
                },
            },
        },
        {
            dataField: 'kod_otklfid_m',
            caption: 'Оч. отключения (мест)',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'otklfid_mname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_otklfid',
                    columns: [
                        {
                            caption: 'kod_otklfid',
                            visible: false,
                            dataField: 'kod_otklfid',
                        },
                        { caption: 'Оч. отключения (мест)', dataField: 'name' },
                    ],
                    api: { select: 'e-grid./ochotkl/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_ochotkl' },
                },
            },
        },
        {
            dataField: 'kod_achr1',
            caption: 'АЧР1',
            width: 150,
            editor: { type: 'ai-popup', text: 'achr1name' },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                id: 'kod_achr1',
                text: 'fname',
                title: 'Выбор АЧР1',
                meta: {
                    title: '',
                    keyExpr: 'kod_achr1',
                    columns: [
                        { caption: 'kod_achr1', visible: false, dataField: 'kod_achr1' },
                        { caption: 'fname', visible: false, dataField: 'fname' },
                        { caption: 'Наименование', dataField: 'name' },
                        { caption: 'Энергорайон', dataField: 'sname' },
                        { caption: 't срабатывания (сек)', dataField: 't' },
                        { caption: 'f срабатывания (Гц)', dataField: 'fgc' },
                    ],
                    api: { select: 'e-grid./achr1/get' },
                },
                args: {},
            },
        },
        {
            dataField: 'kod_spotkl',
            caption: 'Способ отключения',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'spotklname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_spotkl',
                    columns: [
                        { caption: 'kod_spotkl', visible: false, dataField: 'kod_spotkl' },
                        {
                            caption: 'Способ отключения',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./spotkl/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_spotkl' },
                },
            },
        },
        {
            dataField: 'l',
            caption: 'Длина, км',
            width: 150,
            dataType: 'number',
        },
        {
            dataField: 'kodspos',
            caption: 'Способ прокладки',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'sposname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kodspos',
                    columns: [
                        { caption: 'kodspos', visible: false, dataField: 'kodspos' },
                        {
                            caption: 'Способ прокладки',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./sposobprokl/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hk_sposobprokl' },
                },
            },
        },
        {
            dataField: 'kol_prov',
            caption: 'ВЛ: кол. цепей в фазе, КЛ: кол. жил в фазе',
            width: 150,
            dataType: 'number',
        },
        {
            dataField: 'kod_mat_opor',
            caption: 'Материал опор',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'mat_oporname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'name',
                meta: {
                    keyExpr: 'kod_mat_opor',
                    columns: [
                        { caption: 'kod_mat_opor', visible: false, dataField: 'kod_mat_opor' },
                        {
                            caption: 'Материал опор',
                            dataField: 'name',
                        },
                    ],
                    api: { select: 'e-grid./matopor/get' },
                },
                args: {
                    select_args: { schema: 'e_poteri', table: 'hk_poteri_mat_opor' },
                },
            },
        },
        {
            dataField: 'kod_typetor',
            caption: 'Тип ТОР',
            width: 150,
            editor: {
                type: 'ai-drop-down',
                text: 'typetorname',
            },
            component: {
                path: 'components/ai-grid-ro/ai-grid-ro.vue',
                text: 'typename',
                meta: {
                    keyExpr: 'kod_typetor',
                    columns: [
                        { caption: 'kod_typetor', visible: false, dataField: 'kod_typetor' },
                        {
                            caption: 'Тип ТОР',
                            dataField: 'typename',
                        },
                    ],
                    api: { select: 'e-grid./tor/get' },
                },
                args: {
                    select_args: { schema: 'e_grid', table: 'hs_tor' },
                },
            },
        },
        {
            dataField: 'cnt_tor',
            caption: 'ТОР, шт',
            width: 150,
            dataType: 'number',
        },
        {
            dataField: 'coment',
            caption: 'Комментарий',
        },
        {
            dataField: 'parent_id',
            width: 150,
            dataType: 'number',
            visible: false,
            allowEditing: false,
        },
        {
            dataField: 'deleted',
            width: 150,
            dataType: 'boolean',
            visible: false,
            allowEditing: false,
        },
    ],
    api: {
        select: 'e-grid./eofider/get',
        save: 'e-grid./eofider/set',
    },
    hasntRoute: true,
};
const dotsClick = metaCell => {
    if (metaCell.cellInfo.column.dataField !== 'expl_id') return;
    const changes = eofiderGrid.value.gridInstance.option('editing.changes');
    if (Array.isArray(changes) && changes.length > 0) {
        const changesRow = changes.find(item => item.key === metaCell.cellInfo.key);
        if (changesRow) {
            return changesRow.data;
        }
    }
    return metaCell.cellInfo.data;
};

const onUserDataChanged = async e => {
    const gridChanges = e.changes.gridChanges;
    let isUpdated = false;
    const columns = eofiderGrid.value?.gridInstance.option('columns');
    const balIdCellTemplate = columns.find(col => col.dataField === 'bal_id').editCellTemplate;
    const explIdCellTemplate = columns.find(col => col.dataField === 'expl_id').editCellTemplate;
    gridChanges
        .filter(item => item.type === 'insert')
        .forEach(item => {
            if (!item.data.bal_id) {
                Object.assign(item.data, {
                    bal_id: props.userData.bal_id,
                    bname: props.userData.bname,
                    bkod_separator: props.userData.bkod_separator,
                });
                //если столбец использует cellTemplate, добавленное программно значение так же добавляем в DS шаблона
                if (balIdCellTemplate) {
                    eofiderGrid.value?.addCellTemplateValue(
                        'bal_id',
                        'bal_id',
                        props.userData.bal_id,
                        'bname',
                        props.userData.bname
                    );
                }
                isUpdated = true;
            }
            if (!item.data.expl_id) {
                Object.assign(item.data, {
                    expl_id: props.userData.expl_id,
                    ename: props.userData.ename,
                });
                if (explIdCellTemplate) {
                    eofiderGrid.value?.addCellTemplateValue(
                        'expl_id',
                        'expl_id',
                        props.userData.expl_id,
                        'ename',
                        props.userData.ename
                    );
                }
                isUpdated = true;
            }
        });
    if (isUpdated) {
        eofiderGrid.value?.setUserDataChanges(gridChanges);
    }
};

const lockedRows = ref([]);
onMounted(() => {
    setTimeout(() => {
        lockedRows.value = eofiderGrid.value?.gridInstance
            .option('dataSource')
            .filter(row => row['imported'] === true)
            .map(el => el[meta.keyExpr]);
    }, 500);
});

defineExpose({
    ref: eofiderGrid,
});
</script>
